import { PrivateAboutPagesResponse, PublicAboutPageResponse } from "@authory-packages/core";
import { FakeAboutPagePlaceholder, V4AboutPageContentModeWrapper, V4AboutPageWrapper } from "./V3AboutPageContent.styles";
import { V3Loader } from "../Loader";
import { V3ArticleView } from "../ReusableStyledComponents/V3ArticleView";
import { DropdownOptionCTA } from "../DropdownOptionCTA";
import { V3IconEdit } from "../Icons";
import { PropsWithChildren, useContext, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { VerticalSpacing } from "../VerticalSpacing";
import { useWarnIfUnsavedChanges } from "../../hooks/confirmNavigation";
import { SetFooterVisible } from "../../layout/SideBarLayout/SidebarLayout.context";
import { enhanceIframesAndBuiltInVideos } from "../ContentRenderer/utils";
import { nofollowLinks } from "../../utils/nofollowLinks";

const QuillEditor = dynamic(() => import("../EditArticle/quill-editor/QuillEditor"), { ssr: false });
const V3Dropdown = dynamic(() => import("../Dropdown/V3Dropdown").then((m) => m.V3Dropdown));
const StickyBottomBar = dynamic(() => import("../StickyBottomBar/StickyBottomBar").then((m) => m.StickyBottomBar));

interface ContentDropdownContainerProps extends PropsWithChildren {
    isPrivateView: boolean;
    setIsEditing: (isEditing: boolean) => void;
}

const ContentDropdownContainer = ({ isPrivateView, children, setIsEditing }: ContentDropdownContainerProps) => {
    if (!isPrivateView) return <>{children}</>;

    return (
        <V3Dropdown
            clientPointEnabled
            title="Content Settings"
            renderReferenceElement={() => {
                return <V4AboutPageContentModeWrapper $isPrivateView>{children}</V4AboutPageContentModeWrapper>;
            }}
            renderPopperElement={(_, setActive) => {
                return (
                    <>
                        <DropdownOptionCTA
                            label={"Edit"}
                            icon={<V3IconEdit />}
                            onClick={() => {
                                setIsEditing(true);
                                setActive(false);
                            }}
                        />
                    </>
                );
            }}
        />
    );
};

interface V4AboutPageContentProps {
    userAboutPage?: PublicAboutPageResponse | PrivateAboutPagesResponse;
    isPrivateView: boolean;
    onTemporaryImageUploadHandler?: (file: File) => Promise<string>;
    onAboutPageSubmitContent?: (html: string, id: string) => Promise<void>;
    queryArgs: Record<string, string>;
}

export const V4AboutPageContent = ({
    userAboutPage,
    isPrivateView,
    queryArgs,
    onTemporaryImageUploadHandler,
    onAboutPageSubmitContent
}: V4AboutPageContentProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [aboutQuillPageContent, setAboutPageQuillContent] = useState(userAboutPage?.html || "");
    const [updateSubsmitting, setUpdateSubmitting] = useState(false);
    const [quillUpdated, setQuillUpdated] = useState(false);
    const [aboutPageId, setAboutPageId] = useState(userAboutPage && "id" in userAboutPage ? userAboutPage.id : 0);
    const [renderedContent, setRenderedContent] = useState(userAboutPage?.html || "");

    useEffect(() => {
        if (!userAboutPage) return;

        // for public or private - always update rendered content
        setRenderedContent(enhanceIframesAndBuiltInVideos(userAboutPage.html || ""));

        // If in private view
        if (userAboutPage && "id" in userAboutPage) {
            // if id changed
            if (aboutPageId !== userAboutPage.id) {
                setAboutPageQuillContent(userAboutPage?.html || "");
                setQuillUpdated(false);
                setIsEditing(false);
                setAboutPageId(userAboutPage.id);
            }
        }
    }, [userAboutPage]);

    useEffect(() => {
        if (!userAboutPage) return;

        setRenderedContent(enhanceIframesAndBuiltInVideos(userAboutPage.html || ""));
    }, []);

    useWarnIfUnsavedChanges(quillUpdated && isEditing);

    const setFooterVisible = useContext(SetFooterVisible);
    setFooterVisible(true);

    const onAboutPageSave = async () => {
        setUpdateSubmitting(true);

        try {
            if (onAboutPageSubmitContent) {
                await onAboutPageSubmitContent(aboutQuillPageContent, queryArgs.aboutPage as string);
            }
            setUpdateSubmitting(false);
            setQuillUpdated(false);
            setIsEditing(false);
        } catch {
            setUpdateSubmitting(false);
        }
    };

    return (
        <>
            <V4AboutPageWrapper>
                {!userAboutPage ? (
                    <V3Loader />
                ) : (
                    <>
                        <>
                            {isEditing ? (
                                <>
                                    <VerticalSpacing bottom={80}>
                                        <QuillEditor
                                            placeholder={"Type something here"}
                                            content={aboutQuillPageContent || ""}
                                            onChange={(value: string) => {
                                                setQuillUpdated(true);
                                                setAboutPageQuillContent(value);
                                            }}
                                            onTemporaryImageUploadHandler={async (file: File) => {
                                                if (onTemporaryImageUploadHandler) {
                                                    return onTemporaryImageUploadHandler(file);
                                                }
                                                return "";
                                            }}
                                            onQuillInitialize={() => {}}
                                            setTitleCallback={() => {}}
                                            onImageCompleteCounterHandler={() => {}}
                                        />
                                    </VerticalSpacing>
                                    <StickyBottomBar
                                        isSubmitting={updateSubsmitting}
                                        rightSidebuttonConfirmLabel={"Save changes"}
                                        onConfirmHandler={onAboutPageSave}
                                        onCancelHandler={() => {
                                            setIsEditing(false);
                                        }}
                                    />
                                </>
                            ) : (
                                <VerticalSpacing bottom={80}>
                                    <ContentDropdownContainer isPrivateView={isPrivateView} setIsEditing={setIsEditing}>
                                        <V3ArticleView>
                                            {!!renderedContent.length ? (
                                                <div dangerouslySetInnerHTML={{ __html: nofollowLinks(renderedContent) }} suppressHydrationWarning />
                                            ) : (
                                                <>
                                                    {isPrivateView ? (
                                                        <FakeAboutPagePlaceholder>
                                                            <p>
                                                                Build a Page for anything that deserves its own space: Your resume, client
                                                                testimonials, case studies, rates, or your work process.
                                                            </p>
                                                            <p>Click to edit!</p>
                                                        </FakeAboutPagePlaceholder>
                                                    ) : (
                                                        <></>
                                                    )}
                                                </>
                                            )}
                                        </V3ArticleView>
                                    </ContentDropdownContainer>
                                </VerticalSpacing>
                            )}
                        </>
                    </>
                )}
            </V4AboutPageWrapper>
        </>
    );
};
